import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox, Mini } from "./shared";
import { Sparkles, Calendar, Bot, Hash, Clock, Info, Filter, X, Archive, ArchiveRestore, Trash2, ChevronRight } from "lucide-react";

const pretty = (s) => (s || "general").replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const when = (iso) => { try { return new Date(iso).toLocaleString(); } catch (e) { return iso || "—"; } };

export function AIHistory() {
  const [data, setData] = useState(null);
  const [feature, setFeature] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [view, setView] = useState("active");
  const [detail, setDetail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDelOpen, setBulkDelOpen] = useState(false);

  const load = useCallback(() => {
    const params = { view };
    if (feature) params.feature = feature;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/admin/ai-usage-log", { params }).then((r) => setData(r.data)).catch(() => setData({ items: [], by_feature: {}, total: 0, today: 0, archived_count: 0, filtered: 0 }));
  }, [feature, from, to, view]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [feature, from, to, view]);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = (data?.items?.length || 0) > 0 && data.items.every((i) => selected.has(i.id));
  const selAllToggle = () => setSelected(allSelected ? new Set() : new Set((data?.items || []).map((i) => i.id)));
  const bulkAction = async (action) => {
    const ids = [...selected]; if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/admin/ai-usage-log/bulk", { ids, action });
      toast.success(`${action === "delete" ? "Deleted" : action === "archive" ? "Archived" : "Restored"} ${res.affected} ${res.affected === 1 ? "entry" : "entries"}`);
      setSelected(new Set()); setBulkDelOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBulkBusy(false); }
  };

  const clearFilters = () => { setFeature(""); setFrom(""); setTo(""); };
  const hasFilters = feature || from || to;

  const doArchive = async (it, undo = false) => {
    setBusyId(it.id);
    try {
      await api.post(`/admin/ai-usage-log/${it.id}/archive`, null, { params: undo ? { undo: true } : {} });
      toast.success(undo ? "Entry restored" : "Entry archived");
      setDetail(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };
  const doDelete = async (it) => {
    setBusyId(it.id);
    try {
      await api.delete(`/admin/ai-usage-log/${it.id}`);
      toast.success("Entry deleted");
      setConfirmDel(null); setDetail(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };

  if (!data) return <Loader />;
  const features = Object.entries(data.by_feature || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div>
      <Head title="AI History" desc="Every AI generation counted on your dashboard — drill in, archive or delete individual entries." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <Mini label="Total generations" value={data.total} icon={Sparkles} />
        <Mini label="Today" value={data.today} icon={Clock} />
        <Mini label="Distinct features" value={features.length} icon={Bot} />
        <Mini label="Archived" value={data.archived_count} icon={Archive} />
      </div>

      <div className="flex items-start gap-2 bg-sky-500/10 border border-sky-500/25 px-4 py-3 mb-5 text-sky-200 text-sm" data-testid="aihistory-note">
        <Info size={16} className="mt-0.5 shrink-0" />
        <span>These are the exact entries behind the dashboard's <b>AI Generations</b> count. <b>Archiving</b> hides an entry and removes it from the count (reversible); <b>deleting</b> removes it permanently. Produced text isn't retained for these quick logs (full AI Studio content lives in each asset's version history).</span>
      </div>

      {/* View toggle */}
      <div className="flex gap-1 bg-navy-800 border border-white/10 p-1 mb-4 w-fit" data-testid="aihistory-view">
        <button onClick={() => setView("active")} data-testid="aihistory-view-active"
          className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === "active" ? "bg-gold text-navy" : "text-gray-400 hover:text-white"}`}>Active ({data.total})</button>
        <button onClick={() => setView("archived")} data-testid="aihistory-view-archived"
          className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === "archived" ? "bg-gold text-navy" : "text-gray-400 hover:text-white"}`}>Archived ({data.archived_count})</button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-5" data-testid="aihistory-filters">
        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">Feature</label>
          <select value={feature} onChange={(e) => setFeature(e.target.value)} data-testid="aihistory-feature"
            className="bg-navy-800 border border-white/15 text-white p-2.5 text-sm focus:border-gold focus:outline-none min-w-[160px]">
            <option value="">All features</option>
            {features.map(([f, n]) => <option key={f} value={f}>{pretty(f)} ({n})</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="aihistory-from"
            className="bg-navy-800 border border-white/15 text-white p-2.5 text-sm focus:border-gold focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="aihistory-to"
            className="bg-navy-800 border border-white/15 text-white p-2.5 text-sm focus:border-gold focus:outline-none" />
        </div>
        {hasFilters && (
          <button onClick={clearFilters} data-testid="aihistory-clear"
            className="flex items-center gap-1.5 px-3 py-2.5 text-sm text-gray-400 hover:text-white border border-white/15 hover:border-gold/50 transition-colors">
            <X size={14} /> Clear
          </button>
        )}
        <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-500 self-center">
          <Filter size={13} /> {data.filtered} shown{hasFilters ? ` of ${view === "archived" ? data.archived_count : data.total}` : ""}
        </div>
      </div>

      {data.items.length === 0 ? <EmptyBox text={view === "archived" ? "No archived entries." : "No AI generations match your filters."} /> : (
        <>
        <div className="flex items-center gap-3 mb-2 flex-wrap" data-testid="aihistory-bulk-toolbar">
          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
            <input type="checkbox" className="accent-gold" checked={allSelected} onChange={selAllToggle} data-testid="aihistory-select-all" /> Select all ({data.items.length})
          </label>
          {selected.size > 0 && (
            <div className="flex items-center gap-2" data-testid="aihistory-bulk-bar">
              <span className="text-xs text-gold">{selected.size} selected</span>
              {view === "archived"
                ? <button onClick={() => bulkAction("unarchive")} disabled={bulkBusy} data-testid="aihistory-bulk-unarchive" className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"><ArchiveRestore size={13} /> Restore</button>
                : <button onClick={() => bulkAction("archive")} disabled={bulkBusy} data-testid="aihistory-bulk-archive" className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-50"><Archive size={13} /> Archive</button>}
              <button onClick={() => setBulkDelOpen(true)} disabled={bulkBusy} data-testid="aihistory-bulk-delete" className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-50"><Trash2 size={13} /> Delete</button>
              <button onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-white">Clear</button>
            </div>
          )}
        </div>
        <div className="bg-navy-800 border border-white/5 divide-y divide-white/5" data-testid="aihistory-list">
          {data.items.map((it) => (
            <div key={it.id} className="p-4 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors" data-testid={`aihistory-item-${it.id}`}>
              <input type="checkbox" className="accent-gold shrink-0" checked={selected.has(it.id)} onChange={() => toggleSel(it.id)} data-testid={`aihistory-select-${it.id}`} />
              <button onClick={() => setDetail(it)} className="flex items-center gap-3 min-w-0 text-left flex-1" data-testid={`aihistory-open-${it.id}`}>
                <span className="shrink-0 flex items-center justify-center w-9 h-9 rounded bg-gold/15 text-gold"><Sparkles size={16} /></span>
                <div className="min-w-0">
                  <div className="text-white font-medium truncate">{pretty(it.feature)}</div>
                  <div className="text-gray-500 text-xs truncate flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1"><Calendar size={11} /> {when(it.created_at)}</span>
                    {it.operator && <span className="flex items-center gap-1"><Bot size={11} /> {it.operator}</span>}
                    {it.tone && <span className="px-1.5 py-0.5 border border-white/10 rounded-full">{it.tone}</span>}
                  </div>
                </div>
              </button>
              <div className="shrink-0 flex items-center gap-3">
                <span className="hidden sm:flex items-center gap-1 text-xs text-gray-400" title="Characters produced"><Hash size={12} /> {it.chars.toLocaleString()}</span>
                {it.archived ? (
                  <button onClick={() => doArchive(it, true)} disabled={busyId === it.id} data-testid={`aihistory-unarchive-${it.id}`} title="Restore" className="p-1.5 text-gray-400 hover:text-emerald-400 disabled:opacity-40"><ArchiveRestore size={15} /></button>
                ) : (
                  <button onClick={() => doArchive(it)} disabled={busyId === it.id} data-testid={`aihistory-archive-${it.id}`} title="Archive" className="p-1.5 text-gray-400 hover:text-gold disabled:opacity-40"><Archive size={15} /></button>
                )}
                <button onClick={() => setConfirmDel(it)} disabled={busyId === it.id} data-testid={`aihistory-delete-${it.id}`} title="Delete" className="p-1.5 text-gray-400 hover:text-red-400 disabled:opacity-40"><Trash2 size={15} /></button>
                <button onClick={() => setDetail(it)} className="p-1.5 text-gray-600 hover:text-gold" title="Details"><ChevronRight size={16} /></button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Drill-in detail */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setDetail(null)} data-testid="aihistory-detail-modal">
          <div className="bg-navy border border-white/10 w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-white font-medium"><span className="flex items-center justify-center w-8 h-8 rounded bg-gold/15 text-gold"><Sparkles size={16} /></span> {pretty(detail.feature)}</div>
              <button onClick={() => setDetail(null)} data-testid="aihistory-detail-close" className="text-gray-500 hover:text-white"><X size={18} /></button>
            </div>
            <dl className="text-sm divide-y divide-white/5">
              {[["Feature", pretty(detail.feature)], ["When", when(detail.created_at)], ["Operator", detail.operator || "—"], ["Tone", detail.tone || "—"], ["Size", `${detail.chars.toLocaleString()} characters`], ["Status", detail.archived ? "Archived" : "Active"], ["Entry ID", detail.id]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 py-2"><dt className="text-gray-500">{k}</dt><dd className="text-gray-200 text-right truncate">{v}</dd></div>
              ))}
            </dl>
            <p className="text-gray-500 text-xs">The generated text isn't retained for these quick logs. For full editable content, open the item's asset in AI Studio (where version history is kept).</p>
            <div className="flex gap-2 pt-1">
              {detail.archived ? (
                <button onClick={() => doArchive(detail, true)} disabled={busyId === detail.id} data-testid="aihistory-detail-unarchive" className="flex items-center gap-1.5 px-3 py-2 text-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"><ArchiveRestore size={14} /> Restore</button>
              ) : (
                <button onClick={() => doArchive(detail)} disabled={busyId === detail.id} data-testid="aihistory-detail-archive" className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gold/40 text-gold hover:bg-gold/10 disabled:opacity-50"><Archive size={14} /> Archive</button>
              )}
              <button onClick={() => setConfirmDel(detail)} disabled={busyId === detail.id} data-testid="aihistory-detail-delete" className="flex items-center gap-1.5 px-3 py-2 text-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 ml-auto disabled:opacity-50"><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDel && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)} data-testid="aihistory-confirm-delete">
          <div className="bg-navy border border-red-500/30 w-full max-w-sm p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-red-300 font-medium"><Trash2 size={18} /> Delete this entry?</div>
            <p className="text-gray-400 text-sm">Permanently remove the <b className="text-white">{pretty(confirmDel.feature)}</b> generation from {when(confirmDel.created_at)}. This can't be undone and lowers your total count. To hide it reversibly instead, use Archive.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDel(null)} data-testid="aihistory-confirm-cancel" className="px-3 py-2 text-sm border border-white/15 text-gray-300 hover:text-white">Cancel</button>
              <button onClick={() => doDelete(confirmDel)} disabled={busyId === confirmDel.id} data-testid="aihistory-confirm-yes" className="px-3 py-2 text-sm bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50">Delete permanently</button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk delete confirm */}
      {bulkDelOpen && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setBulkDelOpen(false)} data-testid="aihistory-bulk-confirm">
          <div className="bg-navy border border-red-500/30 w-full max-w-sm p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-red-300 font-medium"><Trash2 size={18} /> Delete {selected.size} {selected.size === 1 ? "entry" : "entries"}?</div>
            <p className="text-gray-400 text-sm">Permanently remove {selected.size} selected generation{selected.size === 1 ? "" : "s"}. This can't be undone and lowers your total count. Records one summarized audit event.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setBulkDelOpen(false)} data-testid="aihistory-bulk-confirm-cancel" className="px-3 py-2 text-sm border border-white/15 text-gray-300 hover:text-white">Cancel</button>
              <button onClick={() => bulkAction("delete")} disabled={bulkBusy} data-testid="aihistory-bulk-confirm-yes" className="px-3 py-2 text-sm bg-red-500 text-white font-medium hover:bg-red-600 disabled:opacity-50">Delete permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
